import { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Stack, Typography, CircularProgress, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Document, Page } from 'react-pdf';
import '@/helpers/preview/setup-pdfjs-worker';
import type { PreviewState } from '@/types/preview.types';

// Set up PDF.js worker for react-pdf (pdfjs-dist v5.x)

const PREVIEWABLE_FILE_TYPES = ['video', 'audio', 'image', 'pdf'] as const;

/** Media types that are previewed natively. presentation/PowerPoint excluded - handled by media-agent. */
function isPreviewableFileType(fileType?: string): fileType is (typeof PREVIEWABLE_FILE_TYPES)[number] {
  return !!fileType && PREVIEWABLE_FILE_TYPES.includes(fileType as (typeof PREVIEWABLE_FILE_TYPES)[number]);
}

interface VideoPreviewProps {
  url: string;
}

function VideoPreview({ url }: VideoPreviewProps) {
  return (
    <Box
      sx={{
        flex: 1,
        backgroundColor: 'black',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {url ? (
        <video
          key={url}
          autoPlay
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            width: 'auto',
            height: 'auto',
            objectFit: 'contain',
            display: 'block',
          }}
          src={url}
        />
      ) : (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          sx={{
            width: '100%',
            height: '100%',
            color: 'grey.700',
            backgroundColor: 'grey.300',
          }}
        >
          <Typography variant="body2">No video URL</Typography>
        </Stack>
      )}
    </Box>
  );
}

interface AudioPreviewProps {
  url: string;
}

function AudioPreview({ url }: AudioPreviewProps) {
  return (
    <Box
      sx={{
        flex: 1,
        backgroundColor: 'black',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      {url ? (
        <audio controls autoPlay style={{ width: '80%', maxWidth: '600px' }} src={url} />
      ) : (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          sx={{ width: '100%', height: '100%', color: 'grey.700', backgroundColor: 'grey.300' }}
        >
          <Typography variant="body2">No audio URL</Typography>
        </Stack>
      )}
    </Box>
  );
}

interface ImagePreviewProps {
  url: string;
}

function ImagePreview({ url }: ImagePreviewProps) {
  return (
    <Box
      sx={{
        flex: 1,
        backgroundColor: 'black',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      {url ? (
        <img
          key={url}
          src={url}
          alt="Media content"
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            width: 'auto',
            height: 'auto',
            objectFit: 'contain',
            display: 'block',
          }}
        />
      ) : (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          sx={{ width: '100%', height: '100%', color: 'grey.700', backgroundColor: 'grey.300' }}
        >
          <Typography variant="body2">No image URL</Typography>
        </Stack>
      )}
    </Box>
  );
}

interface PdfPreviewProps {
  url: string;
}

function PdfPreview({ url }: PdfPreviewProps) {
  const [page, setPage] = useState(1);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [containerHeight, setContainerHeight] = useState(600);
  const containerRef = useRef<HTMLDivElement>(null);

  const onDocumentLoadStart = useCallback(() => {
    setNumPages(null);
    setLoading(true);
    setError(null);
    setPage(1);
  }, []);

  const onDocumentLoadSuccess = useCallback(({ numPages: pages }: { numPages: number }) => {
    setNumPages(pages);
    setLoading(false);
    setError(null);
    setPage(1);
  }, []);

  const onDocumentLoadError = useCallback((err: Error) => {
    console.error('Error loading PDF:', err);
    setError('Failed to load PDF document');
    setLoading(false);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (error || !numPages) return;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        setPage((p) => Math.max(1, p - 1));
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        setPage((p) => Math.min(numPages, p + 1));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [error, numPages]);

  useEffect(() => {
    const updateHeight = () => {
      const height = containerRef.current?.clientHeight ?? 600;
      setContainerHeight(height);
    };
    const timer = setTimeout(updateHeight, 100);
    const ro = new ResizeObserver(updateHeight);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => {
      clearTimeout(timer);
      ro.disconnect();
    };
  }, []);

  if (!url) {
    return (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        sx={{ flex: 1, color: 'grey.700', backgroundColor: 'grey.300' }}
      >
        <Typography variant="body2">No PDF URL</Typography>
      </Stack>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        flex: 1,
        backgroundColor: 'black',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10,
          }}
        >
          <CircularProgress sx={{ color: 'white' }} />
        </Box>
      )}

      {error && (
        <Stack direction="row" alignItems="center" justifyContent="center" sx={{ color: 'grey.300', padding: 4 }}>
          <Typography variant="body2">{error}</Typography>
        </Stack>
      )}

      {!error && (
        <Document
          key={url}
          file={url}
          onLoadStart={onDocumentLoadStart}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading=""
          error=""
        >
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Page
              pageNumber={page}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              height={containerHeight}
              scale={1}
            />
          </Box>
        </Document>
      )}

      {/* Page navigation */}
      {numPages && numPages > 1 && !error && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            position: 'absolute',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            padding: '4px 12px',
            borderRadius: '8px',
            zIndex: 5,
          }}
        >
          <IconButton
            size="small"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            sx={{ color: 'white' }}
            aria-label="Previous page"
          >
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2" sx={{ minWidth: 48, textAlign: 'center' }}>
            {page} / {numPages}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setPage((p) => Math.min(numPages, p + 1))}
            disabled={page >= numPages}
            sx={{ color: 'white' }}
            aria-label="Next page"
          >
            <ChevronRight />
          </IconButton>
        </Stack>
      )}
    </Box>
  );
}

function UnsupportedMedia({ fileType }: { fileType?: string }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="center"
      sx={{
        flex: 1,
        color: 'grey.700',
        backgroundColor: 'grey.300',
      }}
    >
      <Typography variant="body2">
        Preview not available for: {fileType || 'unknown'} (e.g. PowerPoint — shown via media-agent)
      </Typography>
    </Stack>
  );
}

interface MediaPreviewProps {
  state: Extract<PreviewState, { page: 'media' }>;
}

export function MediaPreview({ state }: MediaPreviewProps) {
  const { media } = state.payload;
  const { fileType, url } = media;

  if (!isPreviewableFileType(fileType)) {
    return <UnsupportedMedia fileType={fileType} />;
  }

  switch (fileType) {
    case 'video':
      return <VideoPreview url={url} />;
    case 'audio':
      return <AudioPreview url={url} />;
    case 'image':
      return <ImagePreview url={url} />;
    case 'pdf':
      return <PdfPreview url={url} />;
    default:
      return <UnsupportedMedia fileType={fileType} />;
  }
}
